import React, { createContext, useEffect, useMemo, useState } from "react";

export const CartContext = createContext({
  cartItems: [],
  wishlist: [],
  addToCart: () => {},
  addCart: () => {},
  removeFromCart: () => {},
  removeCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  toggleWishlist: () => {},
  isInWishlist: () => false,
  cartCount: 0,
});

const readStorage = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The storefront remains functional when storage is unavailable.
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStorage("pningwillow:cart:v1", readStorage("cartItems", []))
  );
  const [wishlist, setWishlist] = useState(() =>
    readStorage("pningwillow:wishlist:v1", [])
  );

  useEffect(() => {
    writeStorage("pningwillow:cart:v1", cartItems);
  }, [cartItems]);

  useEffect(() => {
    writeStorage("pningwillow:wishlist:v1", wishlist);
  }, [wishlist]);

  const addToCart = (productId, quantity = 1, size = "", color = "") => {
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    setCartItems((items) => {
      const index = items.findIndex(
        (item) =>
          item.id === Number(productId) &&
          item.size === size &&
          item.color === color
      );

      if (index === -1) {
        return [
          ...items,
          { id: Number(productId), size, color, quantity: safeQuantity },
        ];
      }

      return items.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, quantity: item.quantity + safeQuantity }
          : item
      );
    });
  };

  const removeFromCart = (productId, size = "", color = "") => {
    setCartItems((items) =>
      items.filter(
        (item) =>
          !(
            item.id === Number(productId) &&
            item.size === size &&
            item.color === color
          )
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    setCartItems((items) =>
      items.map((item) =>
        item.id === Number(productId) &&
        item.size === size &&
        item.color === color
          ? { ...item, quantity: safeQuantity }
          : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const addToWishlist = (productId) => {
    const id = Number(productId);
    setWishlist((items) => (items.includes(id) ? items : [...items, id]));
  };

  const removeFromWishlist = (productId) => {
    const id = Number(productId);
    setWishlist((items) => items.filter((item) => item !== id));
  };

  const toggleWishlist = (productId) => {
    const id = Number(productId);
    setWishlist((items) =>
      items.includes(id)
        ? items.filter((item) => item !== id)
        : [...items, id]
    );
  };

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const value = {
    cartItems,
    wishlist,
    cartCount,
    addToCart,
    addCart: addToCart,
    removeFromCart,
    removeCart: removeFromCart,
    updateQuantity,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist: (productId) => wishlist.includes(Number(productId)),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
